import { VoiceTextarea } from '../components/VoiceTextarea';
import { VoiceInput } from '../components/VoiceInput';
// src/modules/DiningModule.tsx
import React, { useState, useEffect, useRef } from 'react';
import { 
  Utensils, Droplet, AlertTriangle, CheckCircle, XCircle, 
  Clock, Users, Calendar, Upload, ImageIcon,
  Play, Square, FileText, Shield, PenTool, Lock, Unlock,
  Plus, Minus, Trash2, Edit, Save, Eye, EyeOff,
  Coffee, Soup, GlassWater, Beer, Milk, ClipboardList
} from 'lucide-react';

// ─── TYPES ──────────────────────────────────────────────────
interface DietaryProfile {
  id: number;
  resident_name: string;
  room_number: string;
  iddsi_level: string;
  allergens: string;
  risk_status: 'Low' | 'Medium' | 'High - Choking History' | 'High - Severe Dehydration' | 'High - Allergies';
  target_fluid_intake_ml: number;
  fluid_restriction: boolean;
  notes: string;
  created_at: string;
  updated_at: string;
}

interface WeeklyMenu {
  id: number;
  day_of_week: 'Monday' | 'Tuesday' | 'Wednesday' | 'Thursday' | 'Friday' | 'Saturday' | 'Sunday';
  meal_type: 'Breakfast' | 'Lunch' | 'Dinner';
  dish_name: string;
  description: string;
  iddsi_alternatives_mapped: boolean;
  image_url: string;
  image_base64: string;
  chef_verified: boolean;
  created_at: string;
  updated_at: string;
}

interface FluidLog {
  id: number;
  resident_id: number;
  fluid_type: 'Water' | 'Tea' | 'Coffee' | 'Juice' | 'Soup' | 'Milk' | 'Thickened Fluid' | 'Other';
  volume_ml: number;
  logged_at: string;
  logged_by: string;
  notes: string;
}

interface DiningAudit {
  id: number;
  meal_date: string;
  meal_type: 'Breakfast' | 'Lunch' | 'Dinner';
  resident_id: number;
  staff_name: string;
  staff_signature: string;
  core_temperature_celsius: number;
  probe_sanitisation_validated: boolean;
  dining_dignity_choices: string;
  staff_comments: string;
  staff_signed_at: string;
  deputy_name: string;
  deputy_signature: string;
  deputy_verified: boolean;
  deputy_returned_for_amendment: boolean;
  deputy_comments: string;
  deputy_signed_at: string;
  manager_name: string;
  manager_signature: string;
  manager_feedback: string;
  manager_signed_at: string;
  is_fully_locked: boolean;
  created_at: string;
  updated_at: string;
}

// ─── PRE-SEEDED DATA ──────────────────────────────────────
const DEFAULT_PROFILES: DietaryProfile[] = [
  { id: 1, resident_name: 'Margaret Smith', room_number: '104', iddsi_level: 'Level 5 Minced & Moist', allergens: 'Dairy', risk_status: 'High - Choking History', target_fluid_intake_ml: 1500, fluid_restriction: true, notes: 'Fluid restriction in place', created_at: new Date().toISOString(), updated_at: new Date().toISOString() },
  { id: 2, resident_name: 'John Williams', room_number: '108', iddsi_level: 'Level 4 Pureed', allergens: 'None', risk_status: 'High - Severe Dehydration', target_fluid_intake_ml: 1800, fluid_restriction: false, notes: 'Monitor fluid intake closely', created_at: new Date().toISOString(), updated_at: new Date().toISOString() },
  { id: 3, resident_name: 'Patricia Brown', room_number: '112', iddsi_level: 'Regular', allergens: 'Gluten', risk_status: 'High - Allergies', target_fluid_intake_ml: 2000, fluid_restriction: false, notes: 'Gluten-free diet required', created_at: new Date().toISOString(), updated_at: new Date().toISOString() },
  { id: 4, resident_name: 'James O\'Connor', room_number: '101', iddsi_level: 'Level 6 Soft & Bite-Sized', allergens: 'Shellfish', risk_status: 'Medium', target_fluid_intake_ml: 2000, fluid_restriction: false, notes: '', created_at: new Date().toISOString(), updated_at: new Date().toISOString() },
  { id: 5, resident_name: 'Emma Thompson', room_number: '203', iddsi_level: 'Level 3 Liquidised', allergens: 'None', risk_status: 'High - Choking History', target_fluid_intake_ml: 1600, fluid_restriction: true, notes: 'Thickened fluids required', created_at: new Date().toISOString(), updated_at: new Date().toISOString() },
  { id: 6, resident_name: 'Sarah Durham', room_number: '206', iddsi_level: 'Regular', allergens: 'None', risk_status: 'Low', target_fluid_intake_ml: 2000, fluid_restriction: false, notes: '', created_at: new Date().toISOString(), updated_at: new Date().toISOString() },
  { id: 7, resident_name: 'Samrina Khurram', room_number: '210', iddsi_level: 'Level 5 Minced & Moist', allergens: 'Dairy, Gluten', risk_status: 'High - Allergies', target_fluid_intake_ml: 1800, fluid_restriction: false, notes: 'Dairy and gluten-free', created_at: new Date().toISOString(), updated_at: new Date().toISOString() },
  { id: 8, resident_name: 'Afzal Sadiq', room_number: '215', iddsi_level: 'Regular', allergens: 'None', risk_status: 'Low', target_fluid_intake_ml: 2000, fluid_restriction: false, notes: '', created_at: new Date().toISOString(), updated_at: new Date().toISOString() },
];

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const MEAL_TYPES = ['Breakfast', 'Lunch', 'Dinner'];
const FLUID_TYPES = ['Water', 'Tea', 'Coffee', 'Juice', 'Soup', 'Milk', 'Thickened Fluid', 'Other'];
const FLUID_VOLUMES = [150, 200, 250, 300, 400, 500];

// ─── MAIN COMPONENT ────────────────────────────────────────
export function DiningModule({ user }: { user: any }) {
  const [activeTab, setActiveTab] = useState<'dashboard' | 'menu' | 'fluids' | 'audits'>('dashboard');
  const [profiles, setProfiles] = useState<DietaryProfile[]>([]);
  const [menus, setMenus] = useState<WeeklyMenu[]>([]);
  const [fluidLogs, setFluidLogs] = useState<FluidLog[]>([]);
  const [audits, setAudits] = useState<DiningAudit[]>([]);
  const [selectedResident, setSelectedResident] = useState<number | null>(null);
  const [selectedMenuDay, setSelectedMenuDay] = useState<string>('Monday');
  const [selectedMealType, setSelectedMealType] = useState<string>('Breakfast');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' | 'info' } | null>(null);

  // ─── State for Fluid Tracker ─────────────────────────────
  const [fluidResidentId, setFluidResidentId] = useState<number | null>(null);
  const [fluidType, setFluidType] = useState('Water');
  const [fluidVolume, setFluidVolume] = useState(200);
  const [fluidNotes, setFluidNotes] = useState('');

  // ─── State for Audits ─────────────────────────────────────
  const [auditStep, setAuditStep] = useState<'staff' | 'deputy' | 'manager'>('staff');
  const [selectedAuditId, setSelectedAuditId] = useState<number | null>(null);
  const [newAudit, setNewAudit] = useState<Partial<DiningAudit>>({
    meal_date: new Date().toISOString().split('T')[0],
    meal_type: 'Lunch',
    resident_id: 1,
    staff_name: '',
    staff_signature: '',
    core_temperature_celsius: 75,
    probe_sanitisation_validated: true,
    dining_dignity_choices: '',
    staff_comments: '',
  });

  // ─── INITIALIZATION ──────────────────────────────────────
  useEffect(() => {
    const storedProfiles = localStorage.getItem('rmacs_dietary_profiles');
    const storedMenus = localStorage.getItem('rmacs_dining_menus');
    const storedFluids = localStorage.getItem('rmacs_fluid_logs');
    const storedAudits = localStorage.getItem('rmacs_dining_audits');

    if (storedProfiles) {
      try { setProfiles(JSON.parse(storedProfiles)); } catch { setProfiles(DEFAULT_PROFILES); }
    } else {
      setProfiles(DEFAULT_PROFILES);
      localStorage.setItem('rmacs_dietary_profiles', JSON.stringify(DEFAULT_PROFILES));
    }

    if (storedMenus) {
      try { setMenus(JSON.parse(storedMenus)); } catch { setMenus([]); }
    } else {
      const sampleMenus: WeeklyMenu[] = [];
      DAYS_OF_WEEK.forEach((day, dayIndex) => {
        MEAL_TYPES.forEach((meal, mealIndex) => {
          const dishes = {
            'Monday': ['Porridge with Fruit Compote', 'Roast Chicken with Vegetables', 'Homemade Vegetable Soup with Bread Roll'],
            'Tuesday': ['Scrambled Eggs on Toast', 'Fish Pie with Green Beans', 'Pasta with Tomato and Basil Sauce'],
            'Wednesday': ['Wheat Biscuits with Milk', 'Lamb Casserole with Root Vegetables', 'Mushroom Risotto'],
            'Thursday': ['Fruit and Yoghurt Parfait', 'Jacket Potato with Cheese and Beans', 'Oriental Chicken Stir-Fry'],
            'Friday': ['Bacon and Egg Muffin', 'Fish and Chips', 'Pizza Margherita'],
            'Saturday': ['Pain au Chocolat', 'Ploughmans Lunch', 'Steak and Ale Pie'],
            'Sunday': ['Full English Breakfast', 'Sunday Roast Beef', 'Cheese and Onion Quiche']
          };
          const descriptions = {
            'Breakfast': 'A nutritious start to the day',
            'Lunch': 'A wholesome midday meal',
            'Dinner': 'A satisfying evening meal'
          };
          sampleMenus.push({
            id: dayIndex * 3 + mealIndex + 1,
            day_of_week: day as any,
            meal_type: meal as any,
            dish_name: dishes[day as keyof typeof dishes]?.[mealIndex] || 'Standard Meal',
            description: descriptions[meal as keyof typeof descriptions] || 'Delicious meal prepared fresh daily',
            iddsi_alternatives_mapped: true,
            image_url: '',
            image_base64: '',
            chef_verified: mealIndex === 0,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          });
        });
      });
      setMenus(sampleMenus);
      localStorage.setItem('rmacs_dining_menus', JSON.stringify(sampleMenus));
    }

    if (storedFluids) {
      try { setFluidLogs(JSON.parse(storedFluids)); } catch { setFluidLogs([]); }
    } else {
      const todayLogs: FluidLog[] = [
        { id: 1, resident_id: 1, fluid_type: 'Water', volume_ml: 200, logged_at: new Date().toISOString(), logged_by: 'Nurse Sarah', notes: '' },
        { id: 2, resident_id: 1, fluid_type: 'Tea', volume_ml: 150, logged_at: new Date().toISOString(), logged_by: 'Nurse Sarah', notes: '' },
        { id: 3, resident_id: 2, fluid_type: 'Water', volume_ml: 250, logged_at: new Date().toISOString(), logged_by: 'Carer John', notes: '' },
        { id: 4, resident_id: 3, fluid_type: 'Juice', volume_ml: 200, logged_at: new Date().toISOString(), logged_by: 'Carer Emma', notes: '' },
      ];
      setFluidLogs(todayLogs);
      localStorage.setItem('rmacs_fluid_logs', JSON.stringify(todayLogs));
    }

    if (storedAudits) {
      try { setAudits(JSON.parse(storedAudits)); } catch { setAudits([]); }
    } else {
      setAudits([]);
      localStorage.setItem('rmacs_dining_audits', JSON.stringify([]));
    }
  }, []);

  // ─── PERSISTENCE HELPERS ─────────────────────────────────
  const saveProfiles = (data: DietaryProfile[]) => {
    localStorage.setItem('rmacs_dietary_profiles', JSON.stringify(data));
    setProfiles(data);
  };

  const saveMenus = (data: WeeklyMenu[]) => {
    localStorage.setItem('rmacs_dining_menus', JSON.stringify(data));
    setMenus(data);
  };

  const saveFluidLogs = (data: FluidLog[]) => {
    localStorage.setItem('rmacs_fluid_logs', JSON.stringify(data));
    setFluidLogs(data);
  };

  const saveAudits = (data: DiningAudit[]) => {
    localStorage.setItem('rmacs_dining_audits', JSON.stringify(data));
    setAudits(data);
  };

  // ─── FLUID TRACKING ──────────────────────────────────────
  const addFluidLog = (residentId: number, fluidType: string, volumeMl: number, notes: string = '') => {
    const newLog: FluidLog = {
      id: Date.now() + Math.random(),
      resident_id: residentId,
      fluid_type: fluidType as any,
      volume_ml: volumeMl,
      logged_at: new Date().toISOString(),
      logged_by: user?.name || 'Unknown',
      notes,
    };
    saveFluidLogs([newLog, ...fluidLogs]);
    setMessage({ text: `✅ ${volumeMl}ml ${fluidType} logged successfully`, type: 'success' });
    setTimeout(() => setMessage(null), 3000);
  };

  const getResidentFluidTotal = (residentId: number): number => {
    const today = new Date().toISOString().split('T')[0];
    return fluidLogs
      .filter(log => log.resident_id === residentId && log.logged_at.startsWith(today))
      .reduce((sum, log) => sum + log.volume_ml, 0);
  };

  const getResidentFluidPercentage = (residentId: number): number => {
    const profile = profiles.find(p => p.id === residentId);
    if (!profile) return 0;
    const total = getResidentFluidTotal(residentId);
    return Math.min(Math.round((total / profile.target_fluid_intake_ml) * 100), 100);
  };

  const isDehydrationWarning = (residentId: number): boolean => {
    const profile = profiles.find(p => p.id === residentId);
    if (!profile) return false;
    const percentage = getResidentFluidPercentage(residentId);
    const now = new Date();
    const isAfter4PM = now.getHours() >= 16;
    return profile.risk_status === 'High - Severe Dehydration' && percentage < 60 && isAfter4PM;
  };

  // ─── MENU PUBLISHER ──────────────────────────────────────
  const updateMenuImage = (day: string, meal: string, imageBase64: string) => {
    const updated = menus.map(m => {
      if (m.day_of_week === day && m.meal_type === meal) {
        return { ...m, image_base64: imageBase64, image_url: '' };
      }
      return m;
    });
    saveMenus(updated);
    setMessage({ text: '✅ Menu image updated', type: 'success' });
    setTimeout(() => setMessage(null), 2000);
  };

  const toggleChefVerification = (day: string, meal: string) => {
    const updated = menus.map(m => {
      if (m.day_of_week === day && m.meal_type === meal) {
        return { ...m, chef_verified: !m.chef_verified };
      }
      return m;
    });
    saveMenus(updated);
  };

  const updateMenuDish = (day: string, meal: string, dishName: string) => {
    const updated = menus.map(m => {
      if (m.day_of_week === day && m.meal_type === meal) {
        return { ...m, dish_name: dishName };
      }
      return m;
    });
    saveMenus(updated);
  };

  // ─── DINING AUDITS ──────────────────────────────────────
  const createAudit = (auditData: Omit<DiningAudit, 'id' | 'created_at' | 'updated_at'>) => {
    const newAudit: DiningAudit = {
      ...auditData,
      id: Date.now() + Math.random(),
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
    saveAudits([newAudit, ...audits]);
    setMessage({ text: '✅ Audit created successfully', type: 'success' });
    setTimeout(() => setMessage(null), 3000);
  };

  const updateAudit = (id: number, updates: Partial<DiningAudit>) => {
    const updated = audits.map(a => {
      if (a.id === id) {
        return { ...a, ...updates, updated_at: new Date().toISOString() };
      }
      return a;
    });
    saveAudits(updated);
  };

  // ─── HELPERS ─────────────────────────────────────────────
  const getRiskColor = (risk: string) => {
    const map: any = {
      'Low': 'bg-green-50 text-green-800 border-green-200',
      'Medium': 'bg-yellow-50 text-yellow-800 border-yellow-200',
      'High - Choking History': 'bg-red-50 text-red-800 border-red-300 animate-pulse',
      'High - Severe Dehydration': 'bg-red-50 text-red-800 border-red-300 animate-pulse',
      'High - Allergies': 'bg-orange-50 text-orange-800 border-orange-300 animate-pulse',
    };
    return map[risk] || 'bg-gray-50 text-gray-800';
  };

  const getStatusBadge = (audit: DiningAudit) => {
    if (audit.is_fully_locked) {
      return <span className="bg-emerald-50 text-emerald-800 border border-emerald-200 px-2 py-0.5 rounded text-xs font-semibold">✅ Locked & Approved</span>;
    }
    if (audit.deputy_verified) {
      return <span className="bg-amber-50 text-amber-800 border border-amber-200 px-2 py-0.5 rounded text-xs font-semibold">⏳ Pending Manager</span>;
    }
    if (audit.staff_signed_at) {
      return <span className="bg-amber-50 text-amber-800 border border-amber-200 px-2 py-0.5 rounded text-xs font-semibold">⏳ Pending Deputy</span>;
    }
    return <span className="bg-gray-50 text-gray-600 border border-gray-200 px-2 py-0.5 rounded text-xs font-semibold">📝 Draft</span>;
  };

  // ─── RENDER DASHBOARD ──────────────────────────────────
  const renderDashboard = () => {
    const highRiskResidents = profiles.filter(p => 
      p.risk_status.startsWith('High -')
    );

    return (
      <div className="space-y-6">
        {/* Risk Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl p-4 border border-[#E1DCCD] shadow-sm">
            <p className="text-sm text-[#6B7A76]">Total Residents</p>
            <p className="text-2xl font-bold text-[#123B36]">{profiles.length}</p>
          </div>
          <div className="bg-red-50 rounded-xl p-4 border border-red-200 shadow-sm">
            <p className="text-sm text-red-700">High Risk (Choking)</p>
            <p className="text-2xl font-bold text-red-800">
              {profiles.filter(p => p.risk_status === 'High - Choking History').length}
            </p>
          </div>
          <div className="bg-orange-50 rounded-xl p-4 border border-orange-200 shadow-sm">
            <p className="text-sm text-orange-700">High Risk (Allergies)</p>
            <p className="text-2xl font-bold text-orange-800">
              {profiles.filter(p => p.risk_status === 'High - Allergies').length}
            </p>
          </div>
          <div className="bg-blue-50 rounded-xl p-4 border border-blue-200 shadow-sm">
            <p className="text-sm text-blue-700">Dehydration Alert</p>
            <p className="text-2xl font-bold text-blue-800">
              {profiles.filter(p => isDehydrationWarning(p.id)).length}
            </p>
          </div>
        </div>

        {/* Filter Buttons */}
        <div className="flex flex-wrap gap-2">
          <button className="px-3 py-1 bg-[#123B36] text-white rounded-lg text-xs">All Residents</button>
          <button className="px-3 py-1 bg-red-100 text-red-800 rounded-lg text-xs">⚠️ High Risk Choking</button>
          <button className="px-3 py-1 bg-orange-100 text-orange-800 rounded-lg text-xs">⚠️ High Risk Allergies</button>
          <button className="px-3 py-1 bg-blue-100 text-blue-800 rounded-lg text-xs">💧 Dehydration Watch</button>
          <button className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-lg text-xs">🔄 Modified IDDSI Diets</button>
        </div>

        {/* Resident List */}
        <div className="bg-white rounded-xl border border-[#E1DCCD] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F7F5EE]">
                <tr>
                  <th className="p-3 text-left font-semibold text-[#123B36]">Resident</th>
                  <th className="p-3 text-left font-semibold text-[#123B36]">Room</th>
                  <th className="p-3 text-left font-semibold text-[#123B36]">IDDSI Level</th>
                  <th className="p-3 text-left font-semibold text-[#123B36]">Allergens</th>
                  <th className="p-3 text-left font-semibold text-[#123B36]">Risk Status</th>
                  <th className="p-3 text-left font-semibold text-[#123B36]">Fluid Intake</th>
                  <th className="p-3 text-left font-semibold text-[#123B36]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {profiles.map(p => {
                  const fluidPercentage = getResidentFluidPercentage(p.id);
                  const isDehydrating = isDehydrationWarning(p.id);
                  
                  return (
                    <tr key={p.id} className={`border-b border-[#E1DCCD] hover:bg-[#F7F5EE] transition ${isDehydrating ? 'bg-red-50 border-red-300' : ''}`}>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{p.resident_name}</span>
                          {p.risk_status.startsWith('High -') && (
                            <span className="flex items-center gap-1 px-2 py-0.5 bg-red-100 text-red-800 rounded-full text-xs font-bold animate-pulse">
                              <AlertTriangle className="w-3 h-3" /> HIGH RISK
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="p-3">{p.room_number}</td>
                      <td className="p-3">
                        <span className={`px-2 py-0.5 rounded text-xs ${p.iddsi_level !== 'Regular' ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-600'}`}>
                          {p.iddsi_level}
                        </span>
                      </td>
                      <td className="p-3">
                        {p.allergens !== 'None' ? (
                          <span className="px-2 py-0.5 bg-red-50 text-red-700 rounded text-xs">{p.allergens}</span>
                        ) : '-'}
                      </td>
                      <td className="p-3">
                        <span className={`px-2 py-0.5 rounded text-xs ${getRiskColor(p.risk_status)}`}>
                          {p.risk_status}
                        </span>
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div 
                              className={`h-full rounded-full transition-all ${fluidPercentage >= 80 ? 'bg-green-500' : fluidPercentage >= 60 ? 'bg-yellow-500' : 'bg-red-500'}`}
                              style={{ width: `${Math.min(fluidPercentage, 100)}%` }}
                            />
                          </div>
                          <span className="text-xs font-medium">{fluidPercentage}%</span>
                          <span className="text-xs text-gray-400">({getResidentFluidTotal(p.id)}/{p.target_fluid_intake_ml}ml)</span>
                        </div>
                        {isDehydrating && (
                          <div className="mt-1 text-xs text-red-600 font-bold animate-pulse">
                            ⚠️ Dehydration Threat: Immediate Fluid Administration & Chart Escalation Required
                          </div>
                        )}
                      </td>
                      <td className="p-3">
                        <div className="flex gap-1">
                          <button 
                            onClick={() => setSelectedResident(p.id)}
                            className="p-1 bg-[#123B36] text-white rounded hover:bg-[#0E2C28] text-xs"
                          >
                            <Droplet className="w-3 h-3" />
                          </button>
                          <button className="p-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-xs">
                            <Utensils className="w-3 h-3" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Fluid Log Modal */}
        {selectedResident && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-xl max-w-md w-full">
              <h3 className="text-lg font-bold mb-4">Add Fluid Intake</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Fluid Type</label>
                  <select id="fluid-type" className="w-full border rounded-lg px-3 py-2">
                    {FLUID_TYPES.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Volume (ml)</label>
                  <select id="fluid-volume" className="w-full border rounded-lg px-3 py-2">
                    {FLUID_VOLUMES.map(v => (
                      <option key={v} value={v}>{v}ml</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Notes</label>
                  <VoiceInput id="fluid-notes" type="text" placeholder="Optional notes" className="w-full border rounded-lg px-3 py-2" />
                </div>
                <div className="flex gap-2">
                  <button 
                    onClick={() => {
                      const type = (document.getElementById('fluid-type') as HTMLSelectElement)?.value;
                      const volume = parseInt((document.getElementById('fluid-volume') as HTMLSelectElement)?.value);
                      const notes = (document.getElementById('fluid-notes') as HTMLInputElement)?.value;
                      if (selectedResident && type && volume) {
                        addFluidLog(selectedResident, type, volume, notes);
                        setSelectedResident(null);
                      }
                    }}
                    className="bg-[#123B36] text-white px-4 py-2 rounded-lg flex-1"
                  >
                    Log Fluid
                  </button>
                  <button onClick={() => setSelectedResident(null)} className="bg-gray-200 px-4 py-2 rounded-lg">
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // ─── RENDER MENU PUBLISHER ──────────────────────────────
  const renderMenuPublisher = () => {
    return (
      <div className="space-y-6">
        <div className="bg-white p-4 rounded-xl border border-[#E1DCCD]">
          <h3 className="text-lg font-bold text-[#123B36] mb-4">7-Day Menu Planner</h3>
          
          <div className="flex flex-wrap gap-2 mb-4">
            {DAYS_OF_WEEK.map(day => (
              <button
                key={day}
                onClick={() => setSelectedMenuDay(day)}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${
                  selectedMenuDay === day ? 'bg-[#123B36] text-white' : 'bg-[#F7F5EE] text-[#123B36] hover:bg-[#E1DCCD]'
                }`}
              >
                {day}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {MEAL_TYPES.map(meal => {
              const menuItem = menus.find(m => m.day_of_week === selectedMenuDay && m.meal_type === meal);
              if (!menuItem) return null;
              return (
                <div key={meal} className="border border-[#E1DCCD] rounded-lg overflow-hidden">
                  <div className="bg-[#F7F5EE] p-3 border-b border-[#E1DCCD]">
                    <h4 className="font-bold text-[#123B36]">{meal}</h4>
                  </div>
                  <div className="p-3 space-y-3">
                    <div>
                      <label className="block text-xs text-gray-500">Dish Name</label>
                      <VoiceInput
                        type="text"
                        value={menuItem.dish_name}
                        onChange={(e) => updateMenuDish(selectedMenuDay, meal, e.target.value)}
                        className="w-full border rounded px-2 py-1 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500">Meal Photo</label>
                      <div className="flex items-center gap-2">
                        {menuItem.image_base64 ? (
                          <div className="relative w-16 h-16 border rounded overflow-hidden">
                            <img src={menuItem.image_base64} alt={menuItem.dish_name} className="w-full h-full object-cover" />
                          </div>
                        ) : (
                          <div className="w-16 h-16 border border-dashed rounded flex items-center justify-center bg-gray-50">
                            <ImageIcon className="w-6 h-6 text-gray-400" />
                          </div>
                        )}
                        <button 
                          onClick={() => {
                            const input = document.createElement('input');
                            input.type = 'file';
                            input.accept = 'image/*';
                            input.onchange = (e) => {
                              const file = (e.target as HTMLInputElement).files?.[0];
                              if (file) {
                                const reader = new FileReader();
                                reader.onload = (ev) => {
                                  const result = ev.target?.result as string;
                                  if (result) {
                                    updateMenuImage(selectedMenuDay, meal, result);
                                  }
                                };
                                reader.readAsDataURL(file);
                              }
                            };
                            input.click();
                          }}
                          className="px-3 py-1 bg-[#123B36] text-white rounded text-xs hover:bg-[#0E2C28]"
                        >
                          <Upload className="w-3 h-3 inline" /> Upload
                        </button>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={menuItem.chef_verified}
                        onChange={() => toggleChefVerification(selectedMenuDay, meal)}
                        className="w-4 h-4 accent-[#123B36]"
                      />
                      <label className="text-xs text-gray-600">
                        I confirm that texture-modified (IDDSI) alternatives have been specifically prepared
                      </label>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-2 py-0.5 rounded ${menuItem.iddsi_alternatives_mapped ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {menuItem.iddsi_alternatives_mapped ? '✅ IDDSI Mapped' : '⚠️ IDDSI Not Mapped'}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="bg-white p-4 rounded-xl border border-[#E1DCCD]">
          <h4 className="font-bold text-[#123B36] mb-3">Menu Preview – {selectedMenuDay}</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {MEAL_TYPES.map(meal => {
              const menuItem = menus.find(m => m.day_of_week === selectedMenuDay && m.meal_type === meal);
              if (!menuItem) return null;
              return (
                <div key={meal} className="border rounded-lg p-3 bg-[#F7F5EE]">
                  {menuItem.image_base64 ? (
                    <img src={menuItem.image_base64} alt={menuItem.dish_name} className="w-full h-32 object-cover rounded-lg mb-2" />
                  ) : (
                    <div className="w-full h-32 bg-gray-200 rounded-lg mb-2 flex items-center justify-center text-gray-400">
                      <Utensils className="w-8 h-8" />
                    </div>
                  )}
                  <h5 className="font-bold text-sm">{menuItem.dish_name}</h5>
                  <p className="text-xs text-gray-500">{menuItem.description}</p>
                  <div className="mt-1 flex gap-1">
                    <span className="text-xs px-1 py-0.5 bg-[#123B36] text-white rounded">{meal}</span>
                    {menuItem.chef_verified && <span className="text-xs px-1 py-0.5 bg-green-600 text-white rounded">Chef Verified</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  // ─── RENDER FLUID TRACKER ──────────────────────────────
  const renderFluidTracker = () => {
    const handleAddFluid = () => {
      if (!fluidResidentId) return alert('Please select a resident');
      addFluidLog(fluidResidentId, fluidType, fluidVolume, fluidNotes);
      setFluidNotes('');
    };

    return (
      <div className="space-y-6">
        <div className="bg-white p-4 rounded-xl border border-[#E1DCCD]">
          <h3 className="text-lg font-bold text-[#123B36] mb-4">Fluid Intake Tracker</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Select Resident</label>
              <select 
                value={fluidResidentId || ''} 
                onChange={(e) => setFluidResidentId(Number(e.target.value))}
                className="w-full border rounded-lg px-3 py-2"
              >
                <option value="">Select Resident</option>
                {profiles.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.resident_name} (Room {p.room_number})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Fluid Type</label>
              <select value={fluidType} onChange={(e) => setFluidType(e.target.value)} className="w-full border rounded-lg px-3 py-2">
                {FLUID_TYPES.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Volume (ml)</label>
              <select value={fluidVolume} onChange={(e) => setFluidVolume(Number(e.target.value))} className="w-full border rounded-lg px-3 py-2">
                {FLUID_VOLUMES.map(v => (
                  <option key={v} value={v}>{v}ml</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">&nbsp;</label>
              <button onClick={handleAddFluid} className="bg-[#123B36] text-white px-4 py-2 rounded-lg w-full hover:bg-[#0E2C28]">
                <Plus className="w-4 h-4 inline" /> Log Fluid
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {profiles.map(p => {
              const total = getResidentFluidTotal(p.id);
              const percentage = getResidentFluidPercentage(p.id);
              const isDehydrating = isDehydrationWarning(p.id);
              return (
                <div 
                  key={p.id} 
                  className={`border rounded-lg p-3 transition ${isDehydrating ? 'border-red-400 bg-red-50' : 'border-[#E1DCCD] bg-white'}`}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium text-sm">{p.resident_name}</p>
                      <p className="text-xs text-gray-500">Room {p.room_number}</p>
                    </div>
                    {isDehydrating && <AlertTriangle className="w-4 h-4 text-red-600 animate-pulse" />}
                  </div>
                  <div className="mt-2">
                    <div className="flex justify-between text-xs">
                      <span>Intake</span>
                      <span className="font-medium">{total} / {p.target_fluid_intake_ml}ml</span>
                    </div>
                    <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden mt-1">
                      <div 
                        className={`h-full rounded-full transition-all ${percentage >= 80 ? 'bg-green-500' : percentage >= 60 ? 'bg-yellow-500' : 'bg-red-500'}`}
                        style={{ width: `${Math.min(percentage, 100)}%` }}
                      />
                    </div>
                    <div className="flex justify-between text-xs mt-1">
                      <span className={percentage >= 80 ? 'text-green-600' : percentage >= 60 ? 'text-yellow-600' : 'text-red-600'}>
                        {percentage}%
                      </span>
                      {p.fluid_restriction && (
                        <span className="text-amber-600 font-medium">⚠️ Fluid Restriction</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-6">
            <h4 className="font-bold text-[#123B36] mb-2">Recent Fluid Logs</h4>
            <div className="space-y-1 max-h-60 overflow-y-auto">
              {fluidLogs.slice(0, 10).map(log => {
                const resident = profiles.find(p => p.id === log.resident_id);
                return (
                  <div key={log.id} className="flex items-center justify-between text-sm border-b border-[#E1DCCD] py-2">
                    <div>
                      <span className="font-medium">{resident?.resident_name || 'Unknown'}</span>
                      <span className="text-gray-500 ml-2">{log.fluid_type}</span>
                    </div>
                    <div>
                      <span className="font-medium">{log.volume_ml}ml</span>
                      <span className="text-gray-400 ml-2">by {log.logged_by}</span>
                      <span className="text-gray-400 ml-2 text-xs">{new Date(log.logged_at).toLocaleTimeString()}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ─── RENDER AUDITS ──────────────────────────────────────
  const renderAudits = () => {
    const handleCreateAudit = () => {
      if (!newAudit.resident_id || !newAudit.staff_name || !newAudit.staff_signature) {
        return alert('Please fill in all required fields');
      }
      createAudit({
        meal_date: newAudit.meal_date || new Date().toISOString().split('T')[0],
        meal_type: newAudit.meal_type as any || 'Lunch',
        resident_id: newAudit.resident_id || 0,
        staff_name: newAudit.staff_name || '',
        staff_signature: newAudit.staff_signature || '',
        core_temperature_celsius: newAudit.core_temperature_celsius || 0,
        probe_sanitisation_validated: newAudit.probe_sanitisation_validated || false,
        dining_dignity_choices: newAudit.dining_dignity_choices || '',
        staff_comments: newAudit.staff_comments || '',
        staff_signed_at: new Date().toISOString(),
        deputy_name: '',
        deputy_signature: '',
        deputy_verified: false,
        deputy_returned_for_amendment: false,
        deputy_comments: '',
        deputy_signed_at: '',
        manager_name: '',
        manager_signature: '',
        manager_feedback: '',
        manager_signed_at: '',
        is_fully_locked: false,
      });
      setNewAudit({
        meal_date: new Date().toISOString().split('T')[0],
        meal_type: 'Lunch',
        resident_id: profiles[0]?.id || 1,
        staff_name: user?.name || '',
        staff_signature: '',
        core_temperature_celsius: 75,
        probe_sanitisation_validated: true,
        dining_dignity_choices: '',
        staff_comments: '',
      });
    };

    return (
      <div className="space-y-6">
        <div className="bg-white p-4 rounded-xl border border-[#E1DCCD]">
          <h3 className="text-lg font-bold text-[#123B36] mb-4">Meal Audit Workflow</h3>
          
          <div className="flex gap-2 mb-4">
            <button 
              onClick={() => setAuditStep('staff')}
              className={`px-4 py-2 rounded-lg text-sm font-semibold ${auditStep === 'staff' ? 'bg-[#123B36] text-white' : 'bg-[#F7F5EE]'}`}
            >
              Tier 1: Staff Entry
            </button>
            <button 
              onClick={() => setAuditStep('deputy')}
              className={`px-4 py-2 rounded-lg text-sm font-semibold ${auditStep === 'deputy' ? 'bg-blue-600 text-white' : 'bg-[#F7F5EE]'}`}
            >
              Tier 2: Deputy Verification
            </button>
            <button 
              onClick={() => setAuditStep('manager')}
              className={`px-4 py-2 rounded-lg text-sm font-semibold ${auditStep === 'manager' ? 'bg-purple-600 text-white' : 'bg-[#F7F5EE]'}`}
            >
              Tier 3: Manager Sign-Off
            </button>
          </div>

          <div className="border-t border-[#E1DCCD] pt-4">
            {auditStep === 'staff' && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Resident</label>
                  <select 
                    value={newAudit.resident_id || ''} 
                    onChange={(e) => setNewAudit({ ...newAudit, resident_id: Number(e.target.value) })}
                    className="w-full border rounded-lg px-3 py-2"
                  >
                    {profiles.map(p => (
                      <option key={p.id} value={p.id}>{p.resident_name} (Room {p.room_number})</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Meal Type</label>
                  <select 
                    value={newAudit.meal_type} 
                    onChange={(e) => setNewAudit({ ...newAudit, meal_type: e.target.value as any })}
                    className="w-full border rounded-lg px-3 py-2"
                  >
                    {MEAL_TYPES.map(m => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Date</label>
                  <input 
                    type="date" 
                    value={newAudit.meal_date} 
                    onChange={(e) => setNewAudit({ ...newAudit, meal_date: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Core Temperature (°C)</label>
                  <input 
                    type="number" 
                    value={newAudit.core_temperature_celsius || 75} 
                    onChange={(e) => setNewAudit({ ...newAudit, core_temperature_celsius: Number(e.target.value) })}
                    className="w-full border rounded-lg px-3 py-2"
                    step="0.1"
                  />
                </div>
                <div className="col-span-2">
                  <label className="flex items-center gap-2">
                    <input 
                      type="checkbox" 
                      checked={newAudit.probe_sanitisation_validated} 
                      onChange={(e) => setNewAudit({ ...newAudit, probe_sanitisation_validated: e.target.checked })}
                      className="w-4 h-4 accent-[#123B36]"
                    />
                    <span className="text-sm">Probe sanitisation validated</span>
                  </label>
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700">Dining Dignity Choices</label>
                  <VoiceTextarea 
                    value={newAudit.dining_dignity_choices || ''} 
                    onChange={(e) => setNewAudit({ ...newAudit, dining_dignity_choices: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2"
                    placeholder="e.g., Resident sat in dining room, appropriate cutlery provided, privacy respected"
                    rows={2}
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700">Staff Comments</label>
                  <VoiceTextarea 
                    value={newAudit.staff_comments || ''} 
                    onChange={(e) => setNewAudit({ ...newAudit, staff_comments: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2"
                    placeholder="Additional observations..."
                    rows={2}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Staff Name</label>
                  <VoiceInput 
                    type="text" 
                    value={newAudit.staff_name || user?.name || ''} 
                    onChange={(e) => setNewAudit({ ...newAudit, staff_name: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Signature (Password)</label>
                  <input 
                    type="password" 
                    value={newAudit.staff_signature || ''} 
                    onChange={(e) => setNewAudit({ ...newAudit, staff_signature: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2"
                    placeholder="Enter your password"
                  />
                </div>
                <div className="col-span-2">
                  <button onClick={handleCreateAudit} className="bg-[#123B36] text-white px-6 py-2 rounded-lg hover:bg-[#0E2C28] w-full">
                    <PenTool className="w-4 h-4 inline" /> Submit Staff Audit
                  </button>
                </div>
              </div>
            )}

            {auditStep === 'deputy' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Select Audit to Verify</label>
                  <select 
                    value={selectedAuditId || ''} 
                    onChange={(e) => setSelectedAuditId(Number(e.target.value))}
                    className="w-full border rounded-lg px-3 py-2"
                  >
                    <option value="">Select Audit</option>
                    {audits.filter(a => !a.is_fully_locked && a.staff_signed_at).map(a => {
                      const resident = profiles.find(p => p.id === a.resident_id);
                      return (
                        <option key={a.id} value={a.id}>
                          {resident?.resident_name} - {a.meal_type} ({new Date(a.meal_date).toLocaleDateString()})
                        </option>
                      );
                    })}
                  </select>
                </div>
                {selectedAuditId && (
                  <div className="bg-[#F7F5EE] p-4 rounded-lg">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium">Temp: {audits.find(a => a.id === selectedAuditId)?.core_temperature_celsius}°C</p>
                        <p className="text-sm text-gray-600">Sanitised: {audits.find(a => a.id === selectedAuditId)?.probe_sanitisation_validated ? '✅ Yes' : '❌ No'}</p>
                        <p className="text-sm text-gray-600">Dignity: {audits.find(a => a.id === selectedAuditId)?.dining_dignity_choices || 'Not recorded'}</p>
                      </div>
                      <div className="flex gap-2">
                        <button 
                          onClick={() => {
                            const audit = audits.find(a => a.id === selectedAuditId);
                            if (audit) {
                              updateAudit(selectedAuditId, {
                                deputy_verified: true,
                                deputy_name: user?.name || 'Deputy',
                                deputy_signature: 'verified',
                                deputy_signed_at: new Date().toISOString(),
                              });
                              alert('✅ Audit verified by Deputy');
                            }
                          }}
                          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                        >
                          <CheckCircle className="w-4 h-4 inline" /> Verify
                        </button>
                        <button 
                          onClick={() => {
                            const reason = prompt('Reason for returning for amendment:');
                            if (reason) {
                              updateAudit(selectedAuditId, {
                                deputy_verified: false,
                                deputy_returned_for_amendment: true,
                                deputy_comments: reason,
                                deputy_name: user?.name || 'Deputy',
                                deputy_signature: 'returned',
                                deputy_signed_at: new Date().toISOString(),
                              });
                              alert('📝 Audit returned for amendment');
                            }
                          }}
                          className="bg-yellow-600 text-white px-4 py-2 rounded-lg hover:bg-yellow-700"
                        >
                          <XCircle className="w-4 h-4 inline" /> Return
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {auditStep === 'manager' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Select Audit to Sign-Off</label>
                  <select 
                    value={selectedAuditId || ''} 
                    onChange={(e) => setSelectedAuditId(Number(e.target.value))}
                    className="w-full border rounded-lg px-3 py-2"
                  >
                    <option value="">Select Audit</option>
                    {audits.filter(a => !a.is_fully_locked && a.deputy_verified).map(a => {
                      const resident = profiles.find(p => p.id === a.resident_id);
                      return (
                        <option key={a.id} value={a.id}>
                          {resident?.resident_name} - {a.meal_type} ({new Date(a.meal_date).toLocaleDateString()})
                        </option>
                      );
                    })}
                  </select>
                </div>
                {selectedAuditId && (
                  <div className="bg-[#F7F5EE] p-4 rounded-lg space-y-3">
                    <div>
                      <p className="font-medium">Audit Details</p>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div><span className="text-gray-500">Staff:</span> {audits.find(a => a.id === selectedAuditId)?.staff_name}</div>
                        <div><span className="text-gray-500">Temp:</span> {audits.find(a => a.id === selectedAuditId)?.core_temperature_celsius}°C</div>
                        <div><span className="text-gray-500">Deputy:</span> {audits.find(a => a.id === selectedAuditId)?.deputy_name || 'N/A'}</div>
                        <div><span className="text-gray-500">Deputy Verified:</span> {audits.find(a => a.id === selectedAuditId)?.deputy_verified ? '✅ Yes' : '❌ No'}</div>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Manager Feedback</label>
                      <VoiceTextarea 
                        id="manager-feedback"
                        className="w-full border rounded-lg px-3 py-2"
                        placeholder="Final review comments..."
                        rows={2}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Manager Signature (Password)</label>
                      <input 
                        id="manager-password"
                        type="password" 
                        className="w-full border rounded-lg px-3 py-2"
                        placeholder="Enter your password to lock"
                      />
                    </div>
                    <button 
                      onClick={() => {
                        const feedback = (document.getElementById('manager-feedback') as HTMLTextAreaElement)?.value;
                        const password = (document.getElementById('manager-password') as HTMLInputElement)?.value;
                        if (!feedback) return alert('Please enter feedback');
                        if (!password) return alert('Please enter your password');
                        updateAudit(selectedAuditId, {
                          manager_name: user?.name || 'Manager',
                          manager_signature: password,
                          manager_feedback: feedback,
                          manager_signed_at: new Date().toISOString(),
                          is_fully_locked: true,
                        });
                        alert('🔒 Audit locked and approved. No further changes can be made.');
                        setSelectedAuditId(null);
                      }}
                      className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 w-full"
                    >
                      <Lock className="w-4 h-4 inline" /> Approve & Lock Audit
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="bg-white p-4 rounded-xl border border-[#E1DCCD]">
          <h4 className="font-bold text-[#123B36] mb-3">Audit History</h4>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F7F5EE]">
                <tr>
                  <th className="p-2 text-left">Resident</th>
                  <th className="p-2 text-left">Meal</th>
                  <th className="p-2 text-left">Date</th>
                  <th className="p-2 text-left">Staff</th>
                  <th className="p-2 text-left">Deputy</th>
                  <th className="p-2 text-left">Manager</th>
                  <th className="p-2 text-left">Status</th>
                </tr>
              </thead>
              <tbody>
                {audits.slice(0, 10).map(a => {
                  const resident = profiles.find(p => p.id === a.resident_id);
                  return (
                    <tr key={a.id} className="border-b border-[#E1DCCD]">
                      <td className="p-2">{resident?.resident_name || 'Unknown'}</td>
                      <td className="p-2">{a.meal_type}</td>
                      <td className="p-2">{new Date(a.meal_date).toLocaleDateString()}</td>
                      <td className="p-2">{a.staff_name} {a.staff_signed_at && '✅'}</td>
                      <td className="p-2">{a.deputy_name || '-'} {a.deputy_verified && '✅'}</td>
                      <td className="p-2">{a.manager_name || '-'} {a.manager_signed_at && '✅'}</td>
                      <td className="p-2">{getStatusBadge(a)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  // ─── MAIN RENDER ──────────────────────────────────────────
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-2xl border border-[#E1DCCD] p-6 flex flex-wrap justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-[#123B36] flex items-center gap-2">
            <Utensils className="w-6 h-6" /> Dining & Dietary Suite
          </h2>
          <p className="text-[#6B7A76]">Dietary risk management, menu publishing, fluid tracking & meal audits</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button 
            onClick={() => setActiveTab('dashboard')} 
            className={`px-4 py-2 rounded-lg text-sm font-semibold ${activeTab === 'dashboard' ? 'bg-[#123B36] text-white' : 'bg-[#F7F5EE] text-[#123B36]'}`}
          >
            <AlertTriangle className="w-4 h-4 inline" /> Dashboard
          </button>
          <button 
            onClick={() => setActiveTab('menu')} 
            className={`px-4 py-2 rounded-lg text-sm font-semibold ${activeTab === 'menu' ? 'bg-[#123B36] text-white' : 'bg-[#F7F5EE] text-[#123B36]'}`}
          >
            <Calendar className="w-4 h-4 inline" /> Menu
          </button>
          <button 
            onClick={() => setActiveTab('fluids')} 
            className={`px-4 py-2 rounded-lg text-sm font-semibold ${activeTab === 'fluids' ? 'bg-[#123B36] text-white' : 'bg-[#F7F5EE] text-[#123B36]'}`}
          >
            <Droplet className="w-4 h-4 inline" /> Fluids
          </button>
          <button 
            onClick={() => setActiveTab('audits')} 
            className={`px-4 py-2 rounded-lg text-sm font-semibold ${activeTab === 'audits' ? 'bg-[#123B36] text-white' : 'bg-[#F7F5EE] text-[#123B36]'}`}
          >
            <ClipboardList className="w-4 h-4 inline" /> Audits
          </button>
        </div>
      </div>

      {/* Messages */}
      {message && (
        <div className={`p-3 rounded-xl text-sm ${message.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : message.type === 'error' ? 'bg-red-50 text-red-800 border border-red-200' : 'bg-blue-50 text-blue-800 border border-blue-200'}`}>
          {message.text}
        </div>
      )}

      {/* Tab Content */}
      {activeTab === 'dashboard' && renderDashboard()}
      {activeTab === 'menu' && renderMenuPublisher()}
      {activeTab === 'fluids' && renderFluidTracker()}
      {activeTab === 'audits' && renderAudits()}
    </div>
  );
}

export default DiningModule;